import { useState, useEffect, useCallback } from 'react';
import { Bug, X, CheckCircle2, XCircle, Clock } from 'lucide-react';

interface TrackerStatus {
  trackerLoaded: boolean;
  trackerVersion: string | null;
  sessionId: string | null;
  lastSession: string | null;
  lastPageview: string | null;
  lastEvent: string | null;
}

const TrackerDebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<TrackerStatus>({
    trackerLoaded: false,
    trackerVersion: null,
    sessionId: null,
    lastSession: null,
    lastPageview: null,
    lastEvent: null,
  });

  const checkStatus = useCallback(() => {
    const w = window as any;
    setStatus({
      trackerLoaded: typeof w.posTrack === 'function',
      trackerVersion: w.posTrackerVersion || null,
      sessionId: sessionStorage.getItem('pos_sid') || sessionStorage.getItem('analytics_session_id') || null,
      lastSession: sessionStorage.getItem('pos_last_session') || null,
      lastPageview: sessionStorage.getItem('pos_last_pageview') || null,
      lastEvent: sessionStorage.getItem('pos_last_event') || null,
    });
  }, []);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 2000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const showPanel = import.meta.env.DEV || new URLSearchParams(window.location.search).get('debug') === 'tracker';
  if (!showPanel) return null;

  const formatTime = (iso: string | null) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleTimeString();
    } catch { return iso; }
  };

  const StatusRow = ({ label, ok, detail }: { label: string; ok: boolean; detail?: string | null }) => (
    <div className="flex items-center gap-2 py-1.5 border-b border-border/30 last:border-0">
      {ok ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-destructive shrink-0" />
      )}
      <span className="text-xs font-medium text-foreground/80">{label}</span>
      {detail && (
        <span className="ml-auto text-[10px] text-muted-foreground font-mono truncate max-w-[140px]">
          {detail}
        </span>
      )}
    </div>
  );

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-[9999] bg-background border border-border rounded-full p-2 shadow-lg hover:bg-accent transition-colors"
        title="Tracker Debug"
      >
        <Bug className="w-4 h-4 text-muted-foreground" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-[9999] bg-background border border-border rounded-lg shadow-xl w-72 text-sm">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-muted-foreground" />
          <span className="font-semibold text-xs text-foreground">Tracker Status</span>
        </div>
        <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-3 py-2">
        <StatusRow label="Tracker loaded" ok={status.trackerLoaded} detail={status.trackerLoaded ? 'posTrack ✓' : '404 / missing'} />
        <StatusRow label="Version" ok={!!status.trackerVersion} detail={status.trackerVersion || 'unknown'} />
        <StatusRow label="Session" ok={!!status.sessionId} detail={status.sessionId?.slice(0, 16)} />
        <StatusRow label="Last session" ok={!!status.lastSession} detail={formatTime(status.lastSession) || 'none'} />
        <StatusRow label="Last pageview" ok={!!status.lastPageview} detail={formatTime(status.lastPageview) || 'none'} />
        <StatusRow label="Last event" ok={!!status.lastEvent} detail={formatTime(status.lastEvent) || 'none'} />
      </div>
      <div className="px-3 py-1.5 border-t border-border">
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>Refreshes every 2s · ?debug=tracker to show</span>
        </div>
      </div>
    </div>
  );
};

export default TrackerDebugPanel;
